export type WindowKind = 'mirror' | 'match';

export type PerfLabel =
  | 'pipe.render' // MediaStreamPipeManager 전체 렌더 루프 — videoFpsSource 미설정 시 (measureAsync)
  | 'pipe.render.local' // localVideoToView 렌더 루프 (measureAsync)
  | 'pipe.render.local-send' // localVideoToSend 렌더 루프 (measureAsync)
  | 'pipe.render.peer' // peerVideo 렌더 루프 (measureAsync)
  | 'pipe.effect.render' // WebGL 이펙트 렌더 (measure)
  | 'pipe.rotate.render' // 캔버스 회전 렌더 (measure)
  | 'pipe.humanvision.render' // HumanVision 메인스레드 처리 (measure)
  | 'pipe.antman.render' // Antman 메인스레드 처리 (measure)
  | 'face.detect.main' // MediaPipe FaceDetector 메인스레드 동기 호출 — worker 미지원/폴백 시에만 기록 (measure)
  | 'face.detect.worker' // 비트맵 생성 + worker 왕복 전체 — worker 사용 시에만 기록 (measureAsync)
  | 'canvas.encode.serverScreening'; // 서버 스크리닝 캔버스 인코딩 완료까지 latency (mark)

/**
 * 측정 의미론 분류.
 * - block: 메인스레드 블록 시간 (sync measure). cost = main-thread ms/s.
 * - wall: frame-to-frame wall-clock (measureAsync). 메인스레드 점유와 분리해서 해석해야 함.
 * - latency: 사용자 인지 latency (mark). 비동기 작업 완료까지의 시간.
 */
export type TraceKind = 'block' | 'wall' | 'latency';

export const PERF_LABEL_KIND: Record<PerfLabel, TraceKind> = {
  'pipe.render': 'wall',
  'pipe.render.local': 'wall',
  'pipe.render.local-send': 'wall',
  'pipe.render.peer': 'wall',
  'pipe.effect.render': 'block',
  'pipe.rotate.render': 'block',
  'pipe.humanvision.render': 'block',
  'pipe.antman.render': 'block',
  'face.detect.main': 'block',
  'face.detect.worker': 'wall',
  'canvas.encode.serverScreening': 'latency',
};

export interface FpsSample {
  fps: number;
  timestamp: number;
}

/**
 * Tracer 라벨 별 누적 통계.
 * 모든 시간 단위는 ms.
 */
export interface TracerStat {
  label: string;
  /** 측정 의미론 — UI는 이 값으로 섹션을 분리해 해석 오류를 방지한다. */
  kind: TraceKind;
  count: number;
  /** 초당 호출 횟수 (count / durationSec) */
  rate: number;
  max: number;
  p50: number;
  p95: number;
  /** 초당 메인스레드 점유 시간 (rate × p50, ms/s) */
  cost: number;
}

export type VideoFpsSource = 'local' | 'local-send' | 'peer';

export type VideoFpsSnapshot = {
  current: number;
  p50: number;
  p95: number;
  p05: number;
  samples: FpsSample[];
};

/**
 * render     = localVideoToView 파이프 체인 완료 FPS — UI: "pipe"
 * sendRender = localVideoToSend 파이프 체인 완료 FPS (local 전용, rotate) — UI: "send"
 * source     = WebRTC 전달/수신 FPS (local: RTCMediaSourceStats → "out", peer: RTCInboundRtpStreamStats → "in")
 * encode     = 인코딩/전송 FPS (local 전용, RTCOutboundRtpStreamStats → "enc")
 */
export type VideoFpsPair = {
  render: VideoFpsSnapshot | null;
  sendRender: VideoFpsSnapshot | null;
  source: VideoFpsSnapshot | null;
  encode: VideoFpsSnapshot | null;
};

export type SegmentFpsSnapshot = {
  p50: number;
  p95: number;
  p05: number;
};

export type SegmentFpsData = {
  pageFps: SegmentFpsSnapshot | null;
  localPipeFps: SegmentFpsSnapshot | null;
  localSendPipeFps: SegmentFpsSnapshot | null;
  localSourceFps: SegmentFpsSnapshot | null;
  localEncodeFps: SegmentFpsSnapshot | null;
  peerInFps: SegmentFpsSnapshot | null;
  peerPipeFps: SegmentFpsSnapshot | null;
};

/**
 * 하나의 측정 window(미러 또는 매치 구간)의 완성된 지표 스냅샷.
 * window는 전환 경계에서 종료되고 리포트로 전송된다.
 */
export interface PerfWindowSnapshot {
  kind: WindowKind;
  /** 'mirror:{startedAtEpoch}' 또는 'match:{matchId}' */
  id: string;
  /** match window일 때 matchId, mirror window일 때 null */
  matchId: string | null;
  /** performance.now 기준 */
  startedAt: number;
  /** Date.now 기준. window dedupe key로 사용한다. */
  startedAtEpoch: number;
  /** 완료된 window는 endedAt이 설정됨, 진행 중이면 null */
  endedAt: number | null;
  durationMs: number;

  fps: VideoFpsSnapshot | null;

  videoFps: {
    local: VideoFpsPair;
    peer: VideoFpsPair;
  };

  tracer: Record<string, TracerStat>;
}
